import './task.css';
import { Button, DatePicker, Divider, Form, Input, Modal, Popover, Select, TimePicker, Tooltip, message } from 'antd';
import {
    AppstoreOutlined, BlockOutlined, CalendarOutlined, CheckCircleFilled, CheckCircleOutlined, ClockCircleOutlined,
    ContactsOutlined, ExportOutlined, FileTextOutlined, FlagFilled, FolderOutlined, LinkOutlined,
    MessageOutlined, NumberOutlined, PlusOutlined, SolutionOutlined, TagsOutlined, UserOutlined,
} from '@ant-design/icons';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { serverFetch } from '@server';
import { showError, toDayjs, toApiDate, fmtDate, fmtDateTime, FMT_TIME } from '@extensions/utils';
import { useDicts } from '@extensions/dicts';
import TagSelect from '@controls/tag-select/tag-select';
import ResizableDrawer from '@controls/resizable-drawer/resizable-drawer';
import TaskComments from './task-comments';
import { TASK_PRIORITIES, assessmentChoice, assessmentFlag } from './task-priority';
import { VERSION_RE, compareVersions, isVersionName } from './task-version';
import UserAvatar from './user-avatar';
import { useWeekPlanIds, WeekPlanStar } from './week-plan';
import { LogoSlot, useContragentLogo } from '../contragents/contragent-logo';

// Ленивый импорт: contragent.js уже импортирует task.js (циклическая зависимость)
const Contragent = lazy(() => import('../contragents/contragent'));

const PRIORITY_COLORS = { red: '#ff4d4f', blue: '#1677ff', orange: '#fa8c16' };

// Приоритет по значениям формы ('unset' | 'yes' | 'no')
const priorityOf = (important, urgent) => {
    if (important === 'unset' || urgent === 'unset' || !important || !urgent) return 0;
    if (important === 'yes') return urgent === 'yes' ? 1 : 2;
    return urgent === 'yes' ? 3 : 4;
};

const priorityColor = (priority) =>
    PRIORITY_COLORS[TASK_PRIORITIES[priority].color] ?? (priority === 4 ? '#8c8c8c' : '#d9d9d9');

const Section = ({ icon, title, extra, className, children }) => (
    <div className={`task-section${className ? ` ${className}` : ''}`}>
        <div className="task-section-title">{icon}{title}{extra && <span className="task-section-extra">{extra}</span>}</div>
        {children}
    </div>
);

// Строка панели свойств: подпись слева, контрол справа
const Prop = ({ icon, label, area, children }) => (
    <div className={`task-prop${area ? ` task-prop-${area}` : ''}`}>
        <span className="task-prop-label">{icon}{label}</span>
        <div className="task-prop-value">{children}</div>
    </div>
);

// Кнопка-переключатель «Выполнено» (контрол формы для isClosed)
const DoneToggle = ({ value, onChange }) => (
    <Button
        shape="round"
        className={`task-done${value ? ' active' : ''}`}
        icon={value ? <CheckCircleFilled /> : <CheckCircleOutlined />}
        onClick={() => onChange?.(!value)}
    >
        {value ? 'Выполнена' : 'Отметить выполненной'}
    </Button>
);

// Приоритет в шапке: цветной флажок, по наведению — выбор квадранта Эйзенхауэра, по клику — выбор и сворачивание
const PriorityPicker = ({ onPick }) => {
    const form = Form.useFormInstance();
    const important = Form.useWatch('isImportant');
    const urgent = Form.useWatch('isUrgent');
    const [open, setOpen] = useState(false);
    const priority = priorityOf(important, urgent);
    const current = TASK_PRIORITIES[priority];

    const pick = (q) => {
        const values = q === priority
            ? { isImportant: 'unset', isUrgent: 'unset' }
            : { isImportant: q <= 2 ? 'yes' : 'no', isUrgent: q === 1 || q === 3 ? 'yes' : 'no' };
        form.setFieldsValue(values);
        onPick?.();
        setOpen(false);
    };

    const content = (
        <div className="task-eisen">
            <span />
            <span className="task-eisen-axis">Срочно</span>
            <span className="task-eisen-axis">Не срочно</span>
            {[1, 2, 3, 4].map((q) => {
                const d = TASK_PRIORITIES[q];
                const cell = (
                    <button
                        key={q}
                        type="button"
                        className={`task-eisen-cell${priority === q ? ' active' : ''}`}
                        style={{ '--cell-color': priorityColor(q) }}
                        onClick={() => pick(q)}
                        aria-pressed={priority === q}
                    >
                        <span className="task-eisen-action">{d.action}</span>
                        <span className="task-eisen-label">{d.label}</span>
                    </button>
                );
                return q === 1 || q === 3 ? [
                    <span key={`a${q}`} className="task-eisen-axis task-eisen-axis-v">{q === 1 ? 'Важно' : 'Не важно'}</span>,
                    cell,
                ] : cell;
            })}
            <div className="task-eisen-hint">
                {priority === 0 ? 'Выберите квадрант, чтобы оценить задачу' : 'Повторный клик снимает оценку'}
            </div>
        </div>
    );

    return (
        <Popover
            content={content}
            trigger="hover"
            placement="bottomLeft"
            open={open}
            onOpenChange={setOpen}
            mouseLeaveDelay={0.2}
            overlayClassName="task-eisen-popover"
        >
            <button
                type="button"
                className={`task-flag${priority === 0 ? ' unset' : ''}`}
                style={{ '--cell-color': priorityColor(priority) }}
                aria-label={`Приоритет: ${current.label}`}
            >
                <FlagFilled />
            </button>
        </Popover>
    );
};

// Быстрый выбор срока
const QuickDeadline = ({ onPick }) => {
    const form = Form.useFormInstance();
    const set = (d) => { form.setFieldsValue({ planDate: d, ...(d ? {} : { planTime: null }) }); onPick?.(); };
    const today = dayjs().startOf('day');
    return (
        <div className="task-quick">
            <button type="button" onClick={() => set(today)}>Сегодня</button>
            <button type="button" onClick={() => set(today.add(1, 'day'))}>Завтра</button>
            <button type="button" onClick={() => set(today.add(7, 'day'))}>Через неделю</button>
            <button type="button" className="muted" onClick={() => set(null)}>Без срока</button>
        </div>
    );
};

// Окно ввода названия нового блока
const BlockPrompt = ({ initial, onCreate, onCancel }) => {
    const [form] = Form.useForm();
    const submit = () => form.validateFields().then((v) => onCreate(v.name.trim())).catch(() => {});
    return (
        <Modal open title="Новый блок" okText="Создать" cancelText="Отмена" onOk={submit} onCancel={onCancel}>
            <Form form={form} layout="vertical" initialValues={{ name: initial }}>
                <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Укажите название' }]}>
                    <Input autoFocus onPressEnter={submit} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

// Окно ввода номера новой версии
const VersionPrompt = ({ initial, onCreate, onCancel }) => {
    const [form] = Form.useForm();
    const submit = () => form.validateFields().then((v) => onCreate(v.name.trim())).catch(() => {});
    return (
        <Modal open title="Новая версия" okText="Создать" cancelText="Отмена" onOk={submit} onCancel={onCancel}>
            <Form form={form} layout="vertical" initialValues={{ name: initial }}>
                <Form.Item
                    name="name"
                    label="Номер версии"
                    rules={[
                        { required: true, message: 'Укажите номер' },
                        { pattern: VERSION_RE, message: 'Формат: 5.4.4.50' },
                    ]}
                >
                    <Input autoFocus placeholder="5.4.4.50" onPressEnter={submit} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

// Карточка задачи. id=0 — новая; contragentId — предзаполненный контрагент
const Task = ({ id, contragentId, onClose, onSaved, onOpenContragent }) => {
    const [currentId, setCurrentId] = useState(id);
    const user = useSelector((s) => s.user);
    const navigate = useNavigate();
    const location = useLocation();
    const { dicts, reloadDicts } = useDicts();
    const [form] = Form.useForm();
    const [item, setItem] = useState(null);
    const [contragentOptions, setContragentOptions] = useState([]);
    const [contragentSearch, setContragentSearch] = useState('');
    const [creatingContragent, setCreatingContragent] = useState(null);
    const [versionSearch, setVersionSearch] = useState('');
    const [versionPrompt, setVersionPrompt] = useState(null);
    const [blockSearch, setBlockSearch] = useState('');
    const [blockPrompt, setBlockPrompt] = useState(null);
    const [dirty, setDirty] = useState(false);
    const weekPlan = useWeekPlanIds();
    const important = Form.useWatch('isImportant', form);
    const urgent = Form.useWatch('isUrgent', form);
    const formContragentId = Form.useWatch('contragentId', form);
    const formProjectId = Form.useWatch('projectId', form);
    const contragentLogo = useContragentLogo(
        formContragentId,
        item?.contragentId,
        item?.contragentLogoFileId,
        item?.contragentLogoName || item?.contragentName,
    );

    const isNew = currentId === 0;
    const accent = priorityColor(priorityOf(important, urgent));

    useEffect(() => setCurrentId(id), [id]);

    useEffect(() => {
        if (isNew) {
            const today = dayjs().startOf('day');
            setItem({
                id: 0,
                docDate: toApiDate(today),
                planDate: null,
                userId: user?.userId ?? 0,
                authorId: user?.userId ?? 0,
                contragentId: contragentId ?? null,
                isClosed: false,
                isImportant: null,
                isUrgent: null,
                projectId: null,
                versionId: null,
                blockId: null,
                tags: [],
            });
        } else {
            serverFetch(`task/${currentId}`)
                .then(setItem)
                .catch(showError);
        }
    }, [currentId, isNew, user, contragentId]);

    // Текущий контрагент в опциях селекта
    useEffect(() => {
        if (item?.contragentId) {
            setContragentOptions((prev) => {
                if (prev.some((o) => o.value === item.contragentId)) return prev;
                return [...prev, { value: item.contragentId, label: item.contragentName || `#${item.contragentId}` }];
            });
        }
    }, [item]);

    useEffect(() => {
        if (!item) return;
        form.setFieldsValue({
            docDate: toDayjs(item.docDate),
            isClosed: item.isClosed,
            isImportant: assessmentChoice(item.isImportant),
            isUrgent: assessmentChoice(item.isUrgent),
            planDate: toDayjs(item.planDate),
            planTime: item.planFinish ? toDayjs(item.planFinish) : null,
            name: item.name,
            comment: item.comment,
            userId: item.userId ?? undefined,
            statusId: item.statusId ?? undefined,
            contragentId: item.contragentId ?? undefined,
            projectId: item.projectId ?? undefined,
            versionId: item.versionId ?? undefined,
            blockId: item.blockId ?? undefined,
            resComment: item.resComment,
            tags: item.tags ?? [],
        });
        setDirty(false);
    }, [item, form]);

    const onContragentSearch = useCallback((text) => {
        setContragentSearch(text);
        if (!text || text.length < 2) return;
        serverFetch(`contragent/search?name=${encodeURIComponent(text)}&limit=20`)
            .then((list) => setContragentOptions(list.map((c) => ({ value: c.id, label: c.name }))))
            .catch(showError);
    }, []);

    const onContragentCreated = (c) => {
        setContragentOptions((prev) => (prev.some((o) => o.value === c.id)
            ? prev
            : [...prev, { value: c.id, label: c.name }]));
        form.setFieldsValue({ contragentId: c.id });
        setDirty(true);
        setCreatingContragent(null);
    };

    const onSave = () => {
        form.validateFields().then((values) => {
            // planFinish = planDate + время
            let planFinish = null;
            if (values.planDate && values.planTime) {
                planFinish = values.planDate
                    .hour(values.planTime.hour())
                    .minute(values.planTime.minute())
                    .second(0);
                planFinish = toApiDate(planFinish);
            }
            const body = {
                ...(item ?? {}),
                docDate: toApiDate(values.docDate),
                isClosed: values.isClosed ?? false,
                isImportant: assessmentFlag(values.isImportant),
                isUrgent: assessmentFlag(values.isUrgent),
                planDate: toApiDate(values.planDate),
                planFinish,
                name: values.name ?? '',
                comment: values.comment ?? '',
                userId: values.userId ?? null,
                statusId: values.statusId ?? null,
                contragentId: values.contragentId ?? null,
                projectId: values.projectId ?? null,
                versionId: values.versionId ?? null,
                blockId: values.blockId ?? null,
                resComment: values.resComment ?? '',
                tags: values.tags ?? [],
            };
            serverFetch(isNew ? 'task' : `task/${currentId}`, {
                method: isNew ? 'POST' : 'PUT',
                bodyData: body,
            })
                .then((saved) => {
                    onSaved?.(saved);
                    weekPlan.reload();
                    if (isNew) {
                        setCurrentId(saved.id);
                        message.success('Задача создана');
                    } else {
                        onClose?.();
                    }
                })
                .catch(showError);
        });
    };

    // Ctrl/Cmd+Enter или Ctrl/Cmd+S — сохранить
    const onKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === 'Enter' || e.key === 's' || e.key === 'ы')) {
            e.preventDefault();
            form.submit();
        }
    };

    const markDirty = () => setDirty(true);

    // При смене проекта сбрасываем версию, если она не из нового проекта
    const onValuesChange = (changed) => {
        if (Object.prototype.hasOwnProperty.call(changed, 'projectId')) {
            const versionId = form.getFieldValue('versionId');
            const version = dicts.versions.find((v) => v.id === versionId);
            const blockId = form.getFieldValue('blockId');
            const block = (dicts.blocks ?? []).find((b) => b.id === blockId);
            const reset = {};
            if (version && version.projectId !== changed.projectId) reset.versionId = undefined;
            if (block && block.projectId !== changed.projectId) reset.blockId = undefined;
            if (Object.keys(reset).length) form.setFieldsValue(reset);
        }
        markDirty();
    };

    const createVersion = (name) => {
        serverFetch('version', {
            method: 'POST',
            bodyData: { projectId: formProjectId, name },
        })
            .then((created) => reloadDicts().then(() => {
                form.setFieldsValue({ versionId: created.id });
                setDirty(true);
                setVersionPrompt(null);
            }))
            .catch(showError);
    };

    const onCreateVersionClick = () => {
        if (isVersionName(versionSearch)) {
            createVersion(versionSearch.trim());
        } else {
            setVersionPrompt(versionSearch.trim());
        }
    };

    const userOpts = dicts.users.map((u) => ({
        value: u.id,
        search: u.name,
        label: <span className="task-opt"><UserAvatar name={u.name} size={20} />{u.name}</span>,
    }));
    const statusOpts = dicts.statuses.map((s) => ({
        value: s.id,
        label: <span className="task-opt"><i className="task-status-dot" style={{ background: s.color || '#bfbfbf' }} />{s.name}</span>,
    }));
    const projectOpts = dicts.projects
        .filter((p) => !p.isClosed || p.id === item?.projectId)
        .map((p) => ({
            value: p.id,
            search: p.name,
            label: <span className="task-opt"><i className="task-status-dot square" style={{ background: p.color || '#bfbfbf' }} />{p.name}</span>,
        }));
    const versionOpts = dicts.versions
        .filter((v) => v.projectId === formProjectId)
        .sort((a, b) => compareVersions(b.name, a.name))
        .map((v) => ({ value: v.id, label: v.name }));
    const blockOpts = (dicts.blocks ?? [])
        .filter((b) => b.projectId === formProjectId)
        .sort((a, b) => (a.idx ?? 0) - (b.idx ?? 0) || (a.name || '').localeCompare(b.name || '', 'ru'))
        .map((b) => ({ value: b.id, label: b.name }));
    const createBlock = (name) => {
        serverFetch('block', {
            method: 'POST',
            bodyData: { projectId: formProjectId, name: name.trim() },
        })
            .then((created) => reloadDicts().then(() => {
                form.setFieldsValue({ blockId: created.id });
                setDirty(true);
                setBlockSearch('');
                setBlockPrompt(null);
            }))
            .catch(showError);
    };

    const onCreateBlockClick = () => {
        const name = blockSearch.trim();
        if (name) createBlock(name);
        else setBlockPrompt('');
    };
    const authorName = item?.authorName ?? dicts.users.find((u) => u.id === item?.authorId)?.name;

    return (
        <ResizableDrawer
            open
            storageKey="taskDrawerWidth"
            defaultWidth={960}
            title={isNew ? 'Новая задача' : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {`Задача №${currentId}`}
                    <WeekPlanStar active={weekPlan.ids.has(currentId)} onClick={() => weekPlan.toggle(currentId)} />
                    <Tooltip title="Копировать ссылку">
                        <Button
                            type="text" size="small" icon={<LinkOutlined />}
                            onClick={() => {
                                navigator.clipboard?.writeText(`${window.location.origin}/tasks?id=${currentId}`);
                                message.success('Ссылка скопирована');
                            }}
                        />
                    </Tooltip>
                    {location.pathname !== '/tasks' && (
                        <Tooltip title="Открыть в журнале задач">
                            <Button
                                type="text" size="small" icon={<ExportOutlined />}
                                onClick={() => { navigate(`/tasks?id=${currentId}`); onClose?.(); }}
                            />
                        </Tooltip>
                    )}
                </span>
            )}
            onClose={() => onClose?.()}
            destroyOnClose
            className="resizable-drawer task-drawer"
        >
            {item && (
                <Form
                    form={form}
                    layout="vertical"
                    className="task-layout"
                    onFinish={onSave}
                    onValuesChange={onValuesChange}
                    onKeyDown={onKeyDown}
                >
                    <Form.Item name="isImportant" hidden><Input /></Form.Item>
                    <Form.Item name="isUrgent" hidden><Input /></Form.Item>
                    <div className="task-scroll" style={{ '--accent': accent }}>
                        <div className="task-hero">
                            <div className="task-hero-top">
                                <PriorityPicker onPick={markDirty} />
                                <Form.Item name="name" rules={[{ required: true, message: 'Укажите наименование' }]}>
                                    <Input.TextArea
                                        className="task-hero-input"
                                        variant="borderless"
                                        autoSize={{ minRows: 1, maxRows: 4 }}
                                        placeholder="Что нужно сделать?"
                                        autoFocus={isNew}
                                        onPressEnter={(e) => { if (!e.shiftKey) e.preventDefault(); }}
                                    />
                                </Form.Item>
                                <Form.Item name="isClosed" noStyle>
                                    <DoneToggle />
                                </Form.Item>
                            </div>
                            <div className="task-meta">
                                {!isNew && <span className="task-chip">#{item.id}</span>}
                                {authorName && (
                                    <span className="task-meta-author"><UserAvatar name={authorName} size={18} />{authorName}</span>
                                )}
                                {item.createdOn && <span>создана {fmtDate(item.createdOn)}</span>}
                                {!isNew && item.modifiedOn && <span>изменена {fmtDateTime(item.modifiedOn)}</span>}
                                <Overdue />
                            </div>
                        </div>

                        <div className="task-section task-props">
                            <div className="task-props-grid">
                                <Prop icon={<AppstoreOutlined />} label="Статус" area="status">
                                    <Form.Item name="statusId" noStyle>
                                        <Select allowClear variant="borderless" placeholder="—" options={statusOpts} />
                                    </Form.Item>
                                </Prop>
                                <Prop icon={<UserOutlined />} label="Исполнитель" area="user">
                                    <Form.Item name="userId" noStyle>
                                        <Select
                                            allowClear showSearch variant="borderless" placeholder="—"
                                            optionFilterProp="search" options={userOpts}
                                        />
                                    </Form.Item>
                                </Prop>
                                <Prop icon={<FolderOutlined />} label="Проект" area="project">
                                    <Form.Item
                                        name="projectId" className="task-prop-field"
                                        rules={[{ required: true, message: 'Выберите проект' }]}
                                    >
                                        <Select
                                            showSearch variant="borderless" placeholder="—"
                                            optionFilterProp="search" options={projectOpts}
                                        />
                                    </Form.Item>
                                </Prop>
                                <Prop icon={<BlockOutlined />} label="Блок" area="block">
                                    <Form.Item name="blockId" noStyle>
                                        <Select
                                            allowClear showSearch variant="borderless"
                                            placeholder={formProjectId ? '—' : 'Сначала выберите проект'}
                                            disabled={!formProjectId}
                                            optionFilterProp="label"
                                            options={blockOpts}
                                            onSearch={setBlockSearch}
                                            popupRender={(menu) => (
                                                <>
                                                    {menu}
                                                    {user?.role <= 10 && (
                                                        <>
                                                            <Divider style={{ margin: '4px 0' }} />
                                                            <Button
                                                                type="text"
                                                                icon={<PlusOutlined />}
                                                                style={{ width: '100%', textAlign: 'left' }}
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={onCreateBlockClick}
                                                            >
                                                                Создать блок{blockSearch.trim() ? ` «${blockSearch.trim()}»` : ''}
                                                            </Button>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Form.Item>
                                </Prop>
                                <Prop icon={<NumberOutlined />} label="Версия" area="version">
                                    <Form.Item name="versionId" noStyle>
                                        <Select
                                            allowClear showSearch variant="borderless"
                                            placeholder={formProjectId ? '—' : 'Сначала выберите проект'}
                                            disabled={!formProjectId}
                                            optionFilterProp="label"
                                            options={versionOpts}
                                            onSearch={setVersionSearch}
                                            popupRender={(menu) => (
                                                <>
                                                    {menu}
                                                    {user?.role <= 10 && (
                                                        <>
                                                            <Divider style={{ margin: '4px 0' }} />
                                                            <Button
                                                                type="text"
                                                                icon={<PlusOutlined />}
                                                                style={{ width: '100%', textAlign: 'left' }}
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={onCreateVersionClick}
                                                            >
                                                                Создать версию{versionSearch ? ` «${versionSearch}»` : ''}
                                                            </Button>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Form.Item>
                                </Prop>
                                <Prop icon={<TagsOutlined />} label="Метки" area="tags">
                                    <Form.Item name="tags" noStyle>
                                        <TagSelect tags={dicts.tags} vid={4} variant="borderless" placeholder="—" />
                                    </Form.Item>
                                </Prop>
                                <Prop icon={<ContactsOutlined />} label="Контрагент" area="contragent">
                                    <div className="task-inline">
                                        {(contragentLogo.fileId || contragentLogo.name) && (
                                            <LogoSlot fileId={contragentLogo.fileId} name={contragentLogo.name} size={28} />
                                        )}
                                        <Form.Item name="contragentId" noStyle>
                                            <Select
                                                allowClear
                                                showSearch
                                                variant="borderless"
                                                filterOption={false}
                                                onSearch={onContragentSearch}
                                                options={contragentOptions}
                                                placeholder="Начните вводить"
                                                style={{ flex: 1, minWidth: 0 }}
                                                popupRender={(menu) => (
                                                    <>
                                                        {menu}
                                                        <Divider style={{ margin: '4px 0' }} />
                                                        <Button
                                                            type="text"
                                                            icon={<PlusOutlined />}
                                                            style={{ width: '100%', textAlign: 'left' }}
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => setCreatingContragent({ name: contragentSearch })}
                                                        >
                                                            Создать контрагента{contragentSearch ? ` «${contragentSearch}»` : ''}
                                                        </Button>
                                                    </>
                                                )}
                                            />
                                        </Form.Item>
                                        <Tooltip title="Создать контрагента">
                                            <Button
                                                type="text" size="small" icon={<PlusOutlined />}
                                                onClick={() => setCreatingContragent({ name: contragentSearch })}
                                            />
                                        </Tooltip>
                                        {onOpenContragent && item.contragentId && (
                                            <Tooltip title="Открыть контрагента">
                                                <Button
                                                    type="text" size="small" icon={<ExportOutlined />}
                                                    onClick={() => onOpenContragent(item.contragentId)}
                                                />
                                            </Tooltip>
                                        )}
                                    </div>
                                </Prop>


                                <Prop icon={<CalendarOutlined />} label="Дата задачи" area="date">
                                    <Form.Item name="docDate" noStyle>
                                        <DatePicker format="DD.MM.YYYY" variant="borderless" style={{ width: '100%' }} />
                                    </Form.Item>
                                </Prop>
                                <Prop icon={<ClockCircleOutlined />} label="Срок" area="deadline">
                                    <div className="task-inline">
                                        <Form.Item name="planDate" noStyle>
                                            <DatePicker format="DD.MM.YYYY" placeholder="Дата" variant="borderless" style={{ flex: 1, minWidth: 0 }} />
                                        </Form.Item>
                                        <Form.Item name="planTime" noStyle>
                                            <TimePicker format={FMT_TIME} placeholder="Время" variant="borderless" style={{ width: 88 }} />
                                        </Form.Item>
                                    </div>
                                </Prop>
                            </div>
                            <QuickDeadline onPick={markDirty} />
                        </div>

                        <Section icon={<FileTextOutlined />} title="Описание">
                            <Form.Item name="comment">
                                <Input.TextArea
                                    variant="filled"
                                    autoSize={{ minRows: 4, maxRows: 14 }}
                                    placeholder="Подробности: что, зачем, критерии готовности…"
                                />
                            </Form.Item>
                        </Section>
                        <Section icon={<SolutionOutlined />} title="Результат">
                            <Form.Item name="resComment">
                                <Input.TextArea
                                    variant="filled"
                                    autoSize={{ minRows: 2, maxRows: 10 }}
                                    placeholder="Что сделано по итогу"
                                />
                            </Form.Item>
                        </Section>
                        <div className="task-actions">
                            <Button type="primary" htmlType="submit" size="large" className="task-ok" disabled={!dirty}>
                                ОК
                                {dirty && <span className="task-dirty-dot" />}
                            </Button>
                            <Button size="large" onClick={() => onClose?.()}>Закрыть</Button>
                            <span className="task-actions-hint">
                                {dirty ? 'Есть несохранённые изменения · Ctrl+Enter — сохранить' : 'Изменений нет'}
                            </span>
                        </div>
                        <Section icon={<MessageOutlined />} title="Обсуждение" className="task-discussion">
                            {item.id > 0
                                ? <TaskComments taskId={item.id} />
                                : <div className="task-empty">Комментарии и файлы станут доступны после создания задачи</div>}
                        </Section>
                    </div>
                </Form>
            )}
            {blockPrompt !== null && (
                <BlockPrompt
                    initial={blockPrompt}
                    onCreate={createBlock}
                    onCancel={() => setBlockPrompt(null)}
                />
            )}
            {versionPrompt !== null && (
                <VersionPrompt
                    initial={versionPrompt}
                    onCreate={createVersion}
                    onCancel={() => setVersionPrompt(null)}
                />
            )}
            {creatingContragent && (
                <Suspense fallback={null}>
                    <Contragent
                        id={0}
                        initial={creatingContragent}
                        onClose={() => setCreatingContragent(null)}
                        onSaved={onContragentCreated}
                    />
                </Suspense>
            )}
        </ResizableDrawer>
    );
};

// Подсказка о просрочке по значениям формы
const Overdue = () => {
    const planDate = Form.useWatch('planDate');
    const planTime = Form.useWatch('planTime');
    const isClosed = Form.useWatch('isClosed');
    if (isClosed || !planDate) return null;
    let deadline = planDate.endOf('day');
    if (planTime) deadline = planDate.hour(planTime.hour()).minute(planTime.minute()).second(0);
    if (!deadline.isBefore(dayjs())) return null;
    return <span className="task-overdue">Просрочена · срок {fmtDateTime(deadline)}</span>;
};

export default Task;
